import { useEffect } from 'react';
import { DARK_THEME_MODE_TYPE, MODULOR_ASSETS_PATH } from 'services/theme';

import { RTLLangs } from '../assets/RTLConfig';
import { preloadedThemeName, preloadedThemePromise } from '../themePreloader';
import { DEFAULT_THEME_NAME, getThemeByBrand, isEmpty } from '../utils';

interface UseThemeInitializationProps {
  brand: string;
  lang: string;
  setIsAppReady: (ready: boolean) => void;
  mode: string;
}

export const useThemeInitialization = (props: UseThemeInitializationProps) => {
  const { brand, lang, setIsAppReady, mode } = props;

  const loadFallBackTheme = async () => {
    try {
      const brand11Module = await import('@betbook/modulor-brands/dist/brand11.js');
      const brand11 = brand11Module.brand11 || brand11Module.default || brand11Module;
      brand11.setNonce(window.__webpack_nonce__);

      document.body.dataset.themeName = DEFAULT_THEME_NAME;
      brand11.toggle(true);

      brand11.light();

      console.log('Fallback theme loaded successfully: brand11');
    } catch (fallbackError) {
      console.error('Failed to load fallback theme:', fallbackError);
    }
  };

  const loadTheme = async (themeName: string) => {
    try {
      // Reuse the preloaded chunk if the theme name matches, otherwise do a fresh import
      const themeModule =
        themeName === preloadedThemeName && (await preloadedThemePromise)
          ? await preloadedThemePromise
          : await import(
              /* webpackChunkName: "[request]" */
              /* webpackInclude: /brand\d+\.js$/ */
              `@betbook/modulor-brands/dist/${themeName}.js`
            );

      const theme = themeModule[themeName] || themeModule.default || themeModule;
      const isDarkTheme = mode === DARK_THEME_MODE_TYPE;
      const isDarkTokensPresent = !isEmpty(theme.getDarkThemeTokens());

      theme.setNonce(window.__webpack_nonce__);

      theme.setModulorDomain(MODULOR_ASSETS_PATH);

      theme.toggle(true);

      if (RTLLangs.includes(lang.toLowerCase())) {
        theme.face('rtl');
      }

      if (isDarkTheme && isDarkTokensPresent) {
        theme.dark();
      } else {
        theme.light();
      }
    } catch (error) {
      console.warn(`Failed to initialize theme for: ${themeName}`, error);
      await loadFallBackTheme();
    }
  };

  useEffect(() => {
    const themeName = getThemeByBrand(brand);
    document.body.dataset.themeName = themeName;

    loadTheme(themeName).finally(() => {
      document.documentElement.removeAttribute('data-mode');
      setIsAppReady(true);
    });
  }, [brand, lang]);
};
